import {
    FeatureAccess,
    PartnerBoardFeature,
    PartnerBoardFeatureRight,
} from '../store/partnerManagerSlice';
import { useMemo, useCallback } from 'react';

interface Params {
    feature_access?: FeatureAccess[];
}

function useFeatureAccess(params: Readonly<Params>) {
    const featureToAccess = useMemo(() => {
        if (!params?.feature_access) {
            return {};
        }
        return params.feature_access.reduce(
            (acc, item) => {
                acc[item.feature] = item.rights;
                return acc;
            },
            {} as Record<PartnerBoardFeature, PartnerBoardFeatureRight>
        );
    }, [params?.feature_access]);

    const hasFeatureAccess = useCallback(
        (feature: PartnerBoardFeature, level: PartnerBoardFeatureRight) => {
            const access = featureToAccess[feature];
            if (!access) {
                return false;
            }

            switch (level) {
                case 'FULL_ACCESS':
                    return access === 'FULL_ACCESS';
                case 'READ_ONLY':
                    return access === 'READ_ONLY' || access === 'FULL_ACCESS';
                default:
                    return false;
            }
        },
        [featureToAccess]
    );

    const canReadFeature = useCallback(
        (feature: PartnerBoardFeature) => {
            return hasFeatureAccess(feature, 'READ_ONLY');
        },
        [hasFeatureAccess]
    );

    const canWriteFeature = useCallback(
        (feature: PartnerBoardFeature) => {
            return hasFeatureAccess(feature, 'FULL_ACCESS');
        },
        [hasFeatureAccess]
    );

    return { hasFeatureAccess, canReadFeature, canWriteFeature };
}

export default useFeatureAccess;
